import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  ArrowUpRight,
  Check,
  Phone,
  Hand,
  FlaskConical,
  Clock,
  Search,
  Star,
} from "lucide-react";
import SEO from "@/components/SEO";
import QuoteForm from "@/components/QuoteForm";
import { COMPANY } from "@/data";
import { ABOUT } from "@/content/apropos";

const IMG = (n) => `${process.env.REACT_APP_BACKEND_URL}/api/gallery-images/${n}.png`;

const ICONS = { Hand, FlaskConical, Clock, Search };

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

/* Counter animation on scroll */
const Counter = ({ target, suffix = "", decimals = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const dur = 1600;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  return (
    <span ref={ref} data-testid="about-counter">
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
};

const APropos = () => (
  <>
    <SEO
      title="À propos · VistaWash — Atelier detailing premium Châtelet & Charleroi"
      description="VistaWash, atelier de detailing à Châtelet en région de Charleroi. Un artisan, une méthode, aucun raccourci. Découvrez notre approche du detailing premium en Belgique."
    />
    <Hero />
    <StorySection />
    <ApproachSection />
    <WorkshopSection />
    <CommitmentsSection />
    <StatsSection />
    <FinalCTA />
    <StickyMobileCTA />
  </>
);

/* ============ HERO ============ */
const Hero = () => (
  <section data-testid="about-hero" className="relative min-h-[92vh] flex items-end pt-40 pb-24 grain bg-black">
    <div className="absolute inset-0 z-0">
      <img
        src={IMG("about_founder")}
        alt="Fondateur VistaWash au travail dans l'atelier de Châtelet"
        loading="eager"
        className="w-full h-full object-cover hero-zoom"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
    </div>

    <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 w-full">
      <motion.div initial="hidden" animate="visible" variants={fadeUp} className="flex items-center gap-3 mb-6">
        <div className="w-12 h-px bg-[#D90000]" />
        <span className="text-[11px] uppercase tracking-[0.35em] text-[#D90000]">
          {ABOUT.hero.kicker}
        </span>
      </motion.div>

      <motion.h1
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        custom={1}
        className="font-impact text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.9] max-w-4xl whitespace-pre-line"
      >
        UN ATELIER. UNE MÉTHODE. <br />
        <span className="text-[#D90000]">AUCUN RACCOURCI.</span>
      </motion.h1>

      <motion.p
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        custom={2}
        className="text-white/75 text-base md:text-lg max-w-2xl mt-8 leading-relaxed"
      >
        {ABOUT.hero.subtitle}
      </motion.p>

      <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="flex flex-wrap gap-4 mt-10">
        <a
          href={ABOUT.hero.ctaPrimary.href}
          data-testid="about-hero-primary-cta"
          className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all border border-[#D90000] hover:border-white"
        >
          {ABOUT.hero.ctaPrimary.label} <ArrowUpRight size={16} />
        </a>
        <a
          href={ABOUT.hero.ctaSecondary.href}
          data-testid="about-hero-secondary-cta"
          className="inline-flex items-center gap-3 border border-white/30 hover:border-white hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all"
        >
          {ABOUT.hero.ctaSecondary.label}
        </a>
      </motion.div>
    </div>
  </section>
);

/* ============ STORY (cinematic emotional chapters) ============ */
const FOUNDER_HANDS = "https://customer-assets-rejwkqb3.emergentagent.net/job_site-gen-40/artifacts/xnnttevm_IMG_8047%202.webp";

const Chapter = ({ image, kicker, lines, align = "left", tint = "warm", testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20%" });
  return (
    <section
      ref={ref}
      data-testid={testid}
      className="relative h-screen w-full overflow-hidden bg-black"
    >
      <motion.img
        src={image}
        alt=""
        loading="lazy"
        initial={{ scale: 1.15 }}
        animate={inView ? { scale: 1 } : {}}
        transition={{ duration: 4.5, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Cinematic vignette + tint */}
      <div
        className={`absolute inset-0 ${
          tint === "cold"
            ? "bg-gradient-to-b from-black/70 via-black/20 to-black"
            : tint === "warm"
              ? "bg-gradient-to-b from-black/40 via-transparent to-black/90"
              : "bg-gradient-to-b from-black/85 via-black/45 to-black"
        }`}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,0.7)_100%)]" />

      <div
        className={`relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-center ${
          align === "right" ? "items-end text-right" : "items-start"
        }`}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.2, delay: 0.5 }}
          className="max-w-2xl"
        >
          {kicker && (
            <div className={`flex items-center gap-3 mb-8 ${align === "right" ? "justify-end" : ""}`}>
              <div className="w-10 h-px bg-[#D90000]" />
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#D90000]/90">
                {kicker}
              </span>
            </div>
          )}
          <div className="space-y-4 md:space-y-6">
            {lines.map((line, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  duration: 1.4,
                  delay: 0.8 + i * 0.35,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={`font-impact text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] ${
                  line.accent ? "text-[#D90000]" : "text-white"
                } ${line.italic ? "italic font-normal" : ""}`}
                style={line.italic ? { fontFamily: "Georgia, serif" } : {}}
              >
                {line.text}
              </motion.p>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const StorySection = () => (
  <div data-testid="about-story" className="relative bg-black">
    <Chapter
      testid="chapter-1"
      image={IMG("emotion_purchase")}
      kicker="Chapitre I · Le jour où"
      tint="warm"
      lines={[
        { text: "Vous vous souvenez." },
        { text: "Le bruit sec des clés.", italic: true },
        { text: "L'odeur du cuir neuf.", italic: true },
        { text: "Cette lumière sur la peinture." },
      ]}
    />

    <Chapter
      testid="chapter-2"
      image={IMG("emotion_reveal")}
      kicker="Chapitre II · Ce qu'elle représente"
      tint="warm"
      align="right"
      lines={[
        { text: "Ce n'est pas qu'une voiture." },
        { text: "C'est un trajet, un été,", italic: true },
        { text: "un premier rendez-vous.", italic: true },
        { text: "Une part de vous.", accent: true },
      ]}
    />

    <Chapter
      testid="chapter-3"
      image={IMG("emotion_faded")}
      kicker="Chapitre III · Sans que vous le remarquiez"
      tint="cold"
      lines={[
        { text: "Puis le temps passe." },
        { text: "Une pluie, une station,", italic: true },
        { text: "un lavage de trop.", italic: true },
        { text: "L'éclat s'efface." },
      ]}
    />

    <Chapter
      testid="chapter-4"
      image={FOUNDER_HANDS}
      kicker="Chapitre IV · Le geste"
      tint="warm"
      align="right"
      lines={[
        { text: "Nous ne nettoyons pas", italic: true },
        { text: "une carrosserie.", italic: true },
        { text: "Nous réveillons", accent: true },
        { text: "un souvenir.", accent: true },
      ]}
    />

    {/* Payoff — full black, single centered statement */}
    <section
      data-testid="chapter-payoff"
      className="relative min-h-[80vh] flex items-center justify-center bg-black px-6 md:px-12 py-24"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D90000]/15 blur-[140px] rounded-full" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative text-center max-w-4xl"
      >
        <div className="flex items-center justify-center gap-3 mb-10">
          <div className="w-10 h-px bg-[#D90000]" />
          <span className="text-[10px] uppercase tracking-[0.4em] text-[#D90000]">
            VistaWash · Châtelet
          </span>
          <div className="w-10 h-px bg-[#D90000]" />
        </div>
        <h2 className="font-impact text-4xl sm:text-5xl md:text-7xl lg:text-[104px] leading-[0.95]">
          REPRENEZ LE VOLANT <br />
          <span className="text-[#D90000]">DE LA PREMIÈRE FOIS.</span>
        </h2>
        <motion.a
          href="#contact"
          onClick={(e) => {
            e.preventDefault();
            document.querySelector('[data-testid="quote-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
          }}
          data-testid="chapter-payoff-cta"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 1 }}
          className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-10 py-6 mt-12 transition-all border border-[#D90000] hover:border-white"
        >
          Réserver ce moment <ArrowUpRight size={16} />
        </motion.a>
      </motion.div>
    </section>
  </div>
);

/* ============ APPROACH ============ */
const ApproachSection = () => (
  <section data-testid="about-approach" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl mb-14">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— {ABOUT.approach.kicker}</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6">
          COMMENT ON <span className="text-[#D90000]">TRAVAILLE.</span>
        </h2>
        <p className="text-white/60 mt-8 leading-relaxed">{ABOUT.approach.subtitle}</p>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {ABOUT.approach.items.map((it, i) => {
          const Icon = ICONS[it.icon] || Hand;
          return (
            <motion.div
              key={it.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              data-testid={`about-approach-${i}`}
              className="p-8 md:p-10 border border-white/10 bg-[#050505] hover:border-[#D90000]/50 transition-colors"
            >
              <div className="w-14 h-14 flex items-center justify-center border border-[#D90000]/40 bg-[#D90000]/10 mb-6">
                <Icon size={22} className="text-[#D90000]" />
              </div>
              <h3 className="font-impact text-2xl md:text-3xl mb-4 leading-tight">{it.title}</h3>
              <p className="text-white/60 text-sm md:text-[15px] leading-relaxed">{it.text}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ============ WORKSHOP GALLERY ============ */
const WorkshopSection = () => (
  <section id="atelier" data-testid="about-workshop" className="py-24 md:py-32 px-6 md:px-12">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl mb-12">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— {ABOUT.workshop.kicker}</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6">
          UN ESPACE PENSÉ <br />
          <span className="text-[#D90000]">POUR LE TRAVAIL PRÉCIS.</span>
        </h2>
        <p className="text-white/60 mt-8 leading-relaxed">{ABOUT.workshop.subtitle}</p>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {ABOUT.workshop.photos.map((p, i) => (
          <motion.figure
            key={p.src}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={i}
            data-testid={`about-workshop-photo-${i}`}
            className="group relative aspect-[4/5] overflow-hidden bg-[#0a0a0a]"
          >
            <img
              src={IMG(p.src)}
              alt={p.caption}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <figcaption className="absolute bottom-0 left-0 right-0 p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D90000] font-bold mb-2">
                0{i + 1}
              </div>
              <p className="text-white text-sm leading-snug">{p.caption}</p>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </div>
  </section>
);

/* ============ COMMITMENTS ============ */
const CommitmentsSection = () => (
  <section data-testid="about-commitments" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
    <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— {ABOUT.commitments.kicker}</span>
          <h2 className="font-impact text-4xl md:text-5xl lg:text-[72px] leading-[0.9] mt-6">
            CE QUE VOUS OBTENEZ <br />
            <span className="text-[#D90000]">À CHAQUE PRESTATION.</span>
          </h2>
          <p className="text-white/60 mt-8 leading-relaxed">{ABOUT.commitments.subtitle}</p>
        </motion.div>
      </div>
      <div className="md:col-span-7">
        <ul className="space-y-4">
          {ABOUT.commitments.items.map((c, i) => (
            <motion.li
              key={c.title}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.06 }}
              data-testid={`about-commit-${i}`}
              className="flex items-start gap-5 p-6 md:p-7 border border-white/10 bg-[#050505] hover:border-[#D90000]/40 transition-colors"
            >
              <div className="shrink-0 w-10 h-10 border border-[#D90000] bg-[#D90000]/10 flex items-center justify-center mt-1">
                <Check size={16} className="text-[#D90000]" />
              </div>
              <div>
                <div className="font-impact text-xl md:text-2xl mb-2 leading-tight">{c.title}</div>
                <p className="text-white/60 text-sm md:text-[15px] leading-relaxed">{c.text}</p>
              </div>
            </motion.li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

/* ============ STATS ============ */
const StatsSection = () => (
  <section data-testid="about-stats" className="py-16 md:py-24 px-6 md:px-12">
    <div className="max-w-[1500px] mx-auto border border-white/10 bg-[#0a0a0a] p-8 md:p-14 grid md:grid-cols-3 gap-8 items-center">
      {ABOUT.stats.map((s, i) => (
        <motion.div
          key={s.label}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: i * 0.1 }}
          data-testid={`about-stat-${i}`}
          className="text-center md:text-left"
        >
          <div className={`font-impact text-6xl md:text-7xl leading-none ${i === 1 ? "text-[#D90000]" : "text-white"}`}>
            <Counter target={s.value} suffix={s.suffix || ""} decimals={s.decimals || 0} />
          </div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-white/60 mt-4">
            {s.label}
          </div>
          {i === 2 && (
            <div className="flex justify-center md:justify-start gap-0.5 mt-3 text-[#FBBC04]">
              {[...Array(5)].map((_, j) => (
                <Star key={j} size={14} fill="currentColor" stroke="none" />
              ))}
            </div>
          )}
        </motion.div>
      ))}
    </div>
  </section>
);

/* ============ FINAL CTA ============ */
const FinalCTA = () => (
  <section id="contact" data-testid="about-final-cta" className="py-24 md:py-32 px-6 md:px-12 bg-gradient-to-b from-[#1a0505] to-black border-t border-[#D90000]/20">
    <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— {ABOUT.finalCta.kicker}</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[84px] leading-[0.9] mt-6 mb-8">
          PASSEZ À L'ATELIER. <br />
          <span className="text-[#D90000]">JUGEZ SUR PLACE.</span>
        </h2>
        <p className="text-white/75 leading-relaxed max-w-md mb-10">{ABOUT.finalCta.subtitle}</p>
        <div className="flex flex-wrap gap-4 mb-10">
          <a
            href="#quote-form"
            data-testid="about-final-devis-cta"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('[data-testid="quote-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
            className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all border border-[#D90000] hover:border-white"
          >
            {ABOUT.finalCta.primary.label} <ArrowUpRight size={16} />
          </a>
          <a
            href={COMPANY.phone_link}
            data-testid="about-final-phone-cta"
            className="inline-flex items-center gap-3 border-2 border-white/30 hover:border-white hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all"
          >
            <Phone size={16} /> {COMPANY.phone_display}
          </a>
        </div>
        <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2">Atelier</div>
            <div className="text-white text-sm">{COMPANY.address_line1}</div>
            <div className="text-white/70 text-sm">{COMPANY.address_line2}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2">Email</div>
            <a href={`mailto:${COMPANY.email}`} className="text-white text-sm hover:text-[#D90000] transition-colors">
              {COMPANY.email}
            </a>
          </div>
        </div>
      </div>

      <div className="md:col-span-7 bg-black/40 border border-white/10 p-8 md:p-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] mb-2">— Formulaire de diagnostic</div>
        <h3 className="font-impact text-3xl md:text-4xl mb-8 leading-tight">
          Parlez-nous de votre véhicule
        </h3>
        <QuoteForm defaultService="Diagnostic" />
      </div>
    </div>
  </section>
);

/* ============ STICKY MOBILE CTA ============ */
const StickyMobileCTA = () => (
  <div
    data-testid="about-sticky-mobile"
    className="md:hidden fixed left-4 right-4 bottom-4 z-30"
  >
    <a
      href="#contact"
      onClick={(e) => {
        e.preventDefault();
        document.querySelector('[data-testid="quote-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }}
      className="flex items-center justify-center gap-3 bg-[#D90000] text-white text-[12px] uppercase tracking-[0.22em] font-bold px-6 py-4 shadow-2xl shadow-[#D90000]/30"
    >
      Demander mon diagnostic <ArrowUpRight size={14} />
    </a>
  </div>
);

export default APropos;
